import { IInitializeROVVideoDto, IROVVideoCategory } from 'src/generated-types/rovvideo'
import { ResumableBlobUpload } from 'src/utils/azureBlobUpload'
import { buildCreateVimeoVideoPayload, CreateVimeoVideoPayload } from 'src/utils/rovVideoUploadPayload'
import { extractVimeoId, ResumableVimeoUpload } from 'src/utils/vimeoUpload'
import { VideoEntry } from './BulkUploadRovDialog.action'

export interface ProcessVimeoVideoDeps {
  category: IROVVideoCategory | null
  isShared: boolean
  customerId: EntityId
  createVimeoPlaceholder: (input: IInitializeROVVideoDto) => Promise<{ data: any }>
  createROVVideo: (input: CreateVimeoVideoPayload) => Promise<unknown>
  updateVideo: (id: string, updates: Partial<VideoEntry>) => void
  resumableUploadsRef: React.MutableRefObject<Map<string, ResumableBlobUpload | ResumableVimeoUpload>>
  onSuccess?: (video: VideoEntry) => void
}

// Vimeo fallback path used per-video when the backend has no Azure video storage configured.
// Ported from the (pre-Azure-migration) staging branch's bulk upload flow: a retry reuses the
// existing Vimeo placeholder/upload URL instead of creating a new one (only skipped if the
// video already made it all the way to a fresh placeholder), and the tus upload instance is
// only discarded when a brand new placeholder is issued — not on every attempt — so a paused
// or failed-mid-upload video resumes from where it left off rather than restarting from zero.
// No screenshot capture/upload — Vimeo generates its own thumbnail asynchronously via webhook.
export const processVimeoVideo = async (video: VideoEntry, deps: ProcessVimeoVideoDeps): Promise<void> => {
  const { updateVideo, resumableUploadsRef } = deps

  try {
    updateVideo(video.id, { status: 'uploading', errorMessage: undefined })

    let uploadUrl = video.vimeoUploadUrl
    let vimeoId = video.vimeoId

    if (!uploadUrl || !vimeoId) {
      const placeholderResponse = await deps.createVimeoPlaceholder({
        fileSize: video.file.size,
        title: video.title,
        description: video.description,
      })
      const responseData = placeholderResponse.data

      if (!responseData?.upload?.upload_link) {
        updateVideo(video.id, {
          status: 'error',
          errorMessage: 'Failed to initialize Vimeo upload',
          failedAtPhase: 'initialize',
        })
        return
      }

      uploadUrl = responseData.upload.upload_link
      vimeoId = extractVimeoId(responseData.player_embed_url, process.env.REACT_APP_VIMEO_URL).split('?')[0]
      updateVideo(video.id, { vimeoUploadUrl: uploadUrl, vimeoId })
      // A new placeholder means a new upload URL — any previous tus instance is now stale.
      resumableUploadsRef.current.delete(video.id)
    }

    if (video.failedAtPhase !== 'create-record') {
      let resumableUpload = resumableUploadsRef.current.get(video.id) as ResumableVimeoUpload | undefined
      if (!resumableUpload) {
        resumableUpload = new ResumableVimeoUpload(video.file, uploadUrl!, {
          onProgress: (loaded, total) => {
            const percentage = total > 0 ? Math.round((loaded / total) * 100) : 0
            updateVideo(video.id, { progress: percentage })
          },
        })
        resumableUploadsRef.current.set(video.id, resumableUpload)
      }

      try {
        await resumableUpload.start()
      } catch (uploadError) {
        updateVideo(video.id, {
          status: 'error',
          errorMessage: uploadError instanceof Error ? uploadError.message : 'Upload failed',
          failedAtPhase: 'upload',
        })
        return
      }

      updateVideo(video.id, { progress: 100 })
    }

    await deps.createROVVideo(
      buildCreateVimeoVideoPayload(
        { title: video.title, description: video.description, category: deps.category, share: deps.isShared },
        vimeoId!,
        {
          mooringId: video.mooringId as EntityId,
          positionId: video.positionId as EntityId,
          mainComponentId: video.mainComponentId as EntityId,
          customerId: deps.customerId,
        },
      ),
    )

    updateVideo(video.id, { status: 'done', failedAtPhase: null })
    deps.onSuccess?.(video)
  } catch (error) {
    console.error(`Failed to upload video ${video.title}:`, error)
    updateVideo(video.id, {
      status: 'error',
      errorMessage: error instanceof Error ? error.message : 'Upload failed',
      failedAtPhase: video.failedAtPhase ?? 'initialize',
    })
  }
}
